import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./i18n/LanguageContext";
import { AuthProvider } from "./lib/auth";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Concept } from "./components/Concept";
import { HowItWorks } from "./components/HowItWorks";
import { TrackRecord } from "./components/TrackRecord";
import { AboutTeaser } from "./components/AboutTeaser";
import { Conditions } from "./components/Conditions";
import { ForWhom } from "./components/ForWhom";
import { FAQ } from "./components/FAQ";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { CookieBanner } from "./components/CookieBanner";
import { LeadMagnet } from "./components/LeadMagnet";
import { WebinarBanner, WebinarPopup, WebinarSection } from "./components/WebinarWidget";
import { SummerSaleBanner } from "./components/SummerSaleBanner";
import { AutumnSaleBanner } from "./components/AutumnSaleBanner";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { captureRefFromUrl } from "./lib/refCapture";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import AGB from "./pages/AGB";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminTrackRecord from "./pages/AdminTrackRecord";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminPartners from "./pages/AdminPartners";
import AdminBlog from "./pages/AdminBlog";
import AdminOnboardingCustomers from "./pages/AdminOnboardingCustomers";
import AdminMailTest from "./pages/AdminMailTest";
import AdminBroadcast from "./pages/AdminBroadcast";
import AdminOverview from "./pages/AdminOverview";
import AdminMasterFeed from "./pages/AdminMasterFeed";
import AdminLinks from "./pages/AdminLinks";
import AdminWeeklyReport from "./pages/AdminWeeklyReport";
import AdminMailEngagement from "./pages/AdminMailEngagement";
import PartnerCoaching from "./pages/PartnerCoaching";
import PartnerDownline from "./pages/PartnerDownline";
import AdminPartnerChangePassword from "./pages/AdminPartnerChangePassword";
import PartnerLogin from "./pages/PartnerLogin";
import PartnerProfile from "./pages/PartnerProfile";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Unsubscribe from "./pages/Unsubscribe";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import About from "./pages/About";
import Register from "./pages/Register";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import InsiderReport from "./pages/InsiderReport";
import WebinarThanks from "./pages/WebinarThanks";
import TechWebinarLanding from "./pages/TechWebinarLanding";
import WebinarFinish from "./pages/WebinarFinish";
import PartnerOnboarding from "./pages/PartnerOnboarding";
import PartnerApplication from "./pages/PartnerApplication";
import ContractSign from "./pages/ContractSign";
import AdminWebinars from "./pages/AdminWebinars";
import AdminPartnerReach from "./pages/AdminPartnerReach";

const RouteTracker = () => {
  const loc = useLocation();
  useEffect(() => {
    captureRefFromUrl();
    trackPageView();
  }, [loc.pathname, loc.search]);
  return null;
};

const Landing = () => (
  <div data-testid="landing-page" className="App min-h-screen bg-[var(--al-bg)]">
    <Navbar />
    <main>
      <Hero />
      <SummerSaleBanner />
      <AutumnSaleBanner />
      <WebinarBanner />
      <Concept />
      <HowItWorks />
      <TrackRecord />
      <AboutTeaser />
      <WebinarSection />
      <Conditions />
      <ForWhom />
      <FAQ />
      <Contact />
    </main>
    <Footer />
    <CookieBanner />
    <LeadMagnet />
    <WebinarPopup />
  </div>
);

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/insider-report" element={<InsiderReport />} />
            <Route path="/insider" element={<InsiderReport />} />
            {/* New short Lead-Magnet links — /ai (generic) and /ai/:slug (auto-partner-attribution).
                /insider-report stays alive for backwards compat with already-sent emails. */}
            <Route path="/ai" element={<InsiderReport />} />
            <Route path="/ai/danke" element={<InsiderReport />} />
            <Route path="/ai/:slug" element={<InsiderReport />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/track-record" element={<AdminTrackRecord />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/partners" element={<AdminPartners />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/onboarding-customers" element={<AdminOnboardingCustomers />} />
            <Route path="/admin/mail-test" element={<AdminMailTest />} />
            <Route path="/admin/broadcast" element={<AdminBroadcast />} />
            <Route path="/admin/uebersicht" element={<AdminOverview />} />
            <Route path="/admin/master-feed" element={<AdminMasterFeed />} />
            <Route path="/admin/webinars" element={<AdminWebinars />} />
            <Route path="/admin/partner-reach" element={<AdminPartnerReach />} />
            <Route path="/admin/links" element={<AdminLinks />} />
            <Route path="/admin/wochenbericht" element={<AdminWeeklyReport />} />
            <Route path="/admin/monatsbericht" element={<AdminWeeklyReport />} />
            <Route path="/admin/mail-engagement" element={<AdminMailEngagement />} />
            <Route path="/admin/partner-change-password" element={<AdminPartnerChangePassword />} />
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner/profile" element={<PartnerProfile />} />
            <Route path="/partner/onboarding" element={<AdminOnboardingCustomers />} />
            <Route path="/partner/change-password" element={<AdminPartnerChangePassword />} />
            <Route path="/partner/links" element={<AdminLinks />} />
            <Route path="/partner/coaching" element={<PartnerCoaching />} />
            <Route path="/partner/downline" element={<PartnerDownline />} />
            <Route path="/partner" element={<PartnerDownline />} />
            <Route path="/admin/coaching" element={<PartnerCoaching />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/onboarding/contract" element={<ContractSign />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register/:partnerSlug" element={<Register />} />
            <Route path="/partneronboarding" element={<PartnerOnboarding />} />
            <Route path="/partneronboarding/:partnerSlug" element={<PartnerOnboarding />} />
            <Route path="/bewerbung" element={<PartnerApplication />} />
            <Route path="/bewerbung/:partnerSlug" element={<PartnerApplication />} />
            <Route path="/webinar" element={<WebinarThanks />} />
            <Route path="/webinar/danke" element={<WebinarThanks />} />
            {/* Parallel page for the Technik-Setup-Call (Adrian). Same UI,
                same WhatsApp-reminder flow, but bound to ``kind=technical``
                so neither page can show the other's webinar. Tech-invite
                mails link here. */}
            <Route path="/webinartech" element={<TechWebinarLanding />} />
            <Route path="/webinartech/danke" element={<WebinarThanks kind="technical" />} />
            <Route path="/webinar/finish" element={<WebinarFinish />} />
            <Route path="/webinar/abschluss" element={<WebinarFinish />} />
          </Routes>
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
